/**
 * TikTok Events API browser adapter (`window.ttq`).
 *
 * Same dedup contract as Meta — `event_id` is forwarded so TikTok's
 * server-side fire from `spicychat-backend` (via metaCapiProvider) and
 * the browser pixel collapse into one event.
 */

import type { EventEnvelope } from "@tracking/events";
import type { PixelAdapter, PixelEventMap } from "@tracking/pixels/types";

type TtqFunction = {
  track: (
    eventName: string,
    payload: Record<string, unknown>,
    options: { event_id: string },
  ) => void;
};

type WindowWithTtq = Window & { ttq?: TtqFunction };

const TIKTOK_EVENT_MAP: PixelEventMap = {
  funnel_landed: "ViewContent",
  funnel_qualified: "ClickButton",
  funnel_account_created: "CompleteRegistration",
  funnel_paid_conversion: "CompletePayment",
};

const resolveTtq = (): TtqFunction | null => {
  if (typeof window === "undefined") return null;
  const candidate = (window as WindowWithTtq).ttq;
  if (candidate === undefined) return null;
  if (typeof candidate.track !== "function") return null;
  return candidate;
};

export const fireTikTokPixel = (envelope: EventEnvelope): void => {
  const ttq = resolveTtq();
  if (ttq === null) return;
  const platformName = TIKTOK_EVENT_MAP[envelope.event.name];
  if (platformName === undefined) return;
  ttq.track(platformName, envelope.event.payload, {
    event_id: envelope.event_id,
  });
};

export const tikTokPixelAdapter: PixelAdapter = {
  platform: "tiktok",
  fire: fireTikTokPixel,
};

export const TIKTOK_PIXEL_EVENT_MAP: PixelEventMap = TIKTOK_EVENT_MAP;
